import { useEffect, useMemo, useState } from 'react';
import { getBreakpoint, isDesktopWidth, isMobileWidth, isTabletWidth } from '../lib/breakpoints';
import type { UseMobileDetectionReturn, ViewportSize } from '../types/mobile-layout';

/**
 * Hook to detect mobile viewport with SSR support
 *
 * @returns Mobile detection state including viewport size and breakpoint
 *
 * @example
 * ```tsx
 * const { isMobile, isDesktop, viewport } = useMobileDetection();
 *
 * if (isMobile) {
 *   return <MobileLayout />;
 * }
 * return <DesktopLayout />;
 * ```
 */
export function useMobileDetection(): UseMobileDetectionReturn {
  // Initialize with safe defaults for SSR
  const [viewport, setViewport] = useState<ViewportSize>(() => {
    if (typeof window !== 'undefined') {
      return {
        width: window.innerWidth,
        height: window.innerHeight,
      };
    }
    // Default to desktop size during SSR
    return {
      width: 1024,
      height: 768,
    };
  });

  useEffect(() => {
    // Skip if SSR (window not available)
    if (typeof window === 'undefined') {
      return;
    }

    // Update viewport on mount
    const handleResize = () => {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    // Initial update
    handleResize();

    // Listen for resize events
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const breakpoint = useMemo(() => getBreakpoint(viewport.width), [viewport.width]);

  const isMobile = useMemo(() => isMobileWidth(viewport.width), [viewport.width]);
  const isTablet = useMemo(() => isTabletWidth(viewport.width), [viewport.width]);
  const isDesktop = useMemo(() => isDesktopWidth(viewport.width), [viewport.width]);

  return {
    viewport,
    isMobile,
    isTablet,
    isDesktop,
    breakpoint,
  };
}

/**
 * Hook specifically for checking if current viewport is mobile
 * Simpler API when you only need mobile detection
 *
 * @returns boolean indicating if viewport is mobile
 *
 * @example
 * ```tsx
 * const isMobile = useIsMobile();
 *
 * return (
 *   <div className={isMobile ? 'mobile' : 'desktop'}>
 *     Content
 *   </div>
 * );
 * ```
 */
export function useIsMobile(): boolean {
  const { isMobile } = useMobileDetection();
  return isMobile;
}
